import {
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  RepositionScrollStrategy,
  RepositionScrollStrategyConfig
} from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, Injector, inject } from '@angular/core';

import { IOverlayConfig } from '../model';
import { OverlayAccessor } from './overlay-accessor';

export const OVERLAY_DEFAULT_CONFIG: IOverlayConfig = {
  isModal: false,
  hasBackdrop: true,
  backdropClass: 'overlay-backdrop',
  panelClass: 'overlay-panel'
};

@Injectable({
  providedIn: 'root'
})
export abstract class OverlayService {
  protected getScrollStrategy(config?: RepositionScrollStrategyConfig): RepositionScrollStrategy {
    return this.overlay.scrollStrategies.reposition(config);
  }

  protected getPosition(config?: IOverlayConfig): GlobalPositionStrategy | FlexibleConnectedPositionStrategy {
    if (config.origin) {
      return this.overlay
        .position()
        .flexibleConnectedTo(config.origin)
        .withPositions(this.getPositionAnchors(config))
        .withFlexibleDimensions(false)
        .withPush(false);
    }

    return this.overlay.position().global().centerHorizontally().centerVertically();
  }

  abstract createInjector(overlayRef: OverlayAccessor, overlayConfig: IOverlayConfig): Injector;

  protected readonly overlay = inject(Overlay);
  protected readonly injector = inject(Injector);

  constructor() {}

  /**
   * open a custom component in an overlay
   */
  open<T>(component: ComponentType<T>, config: IOverlayConfig = {}): OverlayAccessor {
    config = this.extendConfig(config);

    /** create the overlay with customizable options */
    const overlayRef = this.overlay.create(config);

    /** create dialogRef to return */
    const overlayAccessor = new OverlayAccessor(overlayRef);

    const injector = this.createInjector(overlayAccessor, config);

    /** attach component portal to the overlay*/
    const portal = new ComponentPortal(component, null, injector);
    overlayRef.attach(portal);

    return overlayAccessor;
  }

  protected extendConfig(config: IOverlayConfig = {}): IOverlayConfig {
    /** globally centered position strategy */
    const scrollStrategy = this.getScrollStrategy({ autoClose: false, scrollThrottle: 50 });

    const positionStrategy = this.getPosition(config);

    return Object.assign({ ...OVERLAY_DEFAULT_CONFIG }, { positionStrategy }, { scrollStrategy }, { ...config });
  }

  private getPositionAnchors(config: IOverlayConfig): ConnectionPositionPair[] {
    const { origin, offsetX, offsetY } = config;

    return [
      {
        //bottom
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetX: 0,
        offsetY: 20
      },
      {
        //top
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetX: 0,
        offsetY: -20
      },

      {
        //left
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center',
        offsetX: 10,
        offsetY: 0
      },
      {
        //right
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
        offsetX: 10,
        offsetY: 0
      }
    ];
  }
}
